import { useState } from "react"
import { useDrag } from "react-dnd"
import { Trash2, DoorOpen, Cpu, Wrench } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"




// Available marker types for the palette
const markerTypes = [
  { type: "waste", color: "#22C55E", icon: Trash2 },
]



function FacilityDrawer({ isOpen, onToggle, onGeocode, onSave, selectedMarker, onUpdateMarker, onDeleteMarker, onSelectMarkerType, onExitAddMode }) {
  const [address, setAddress] = useState("")
  const [selectedMarkerType, setSelectedMarkerType] = useState(null)

  const handleAddressChange = (e) => setAddress(e.target.value)

  const handleGeocodeClick = () => {
    onGeocode(address)
  }

  const handleAddressKeyPress = (e) => {
    if (e.key === "Enter") {
      onGeocode(address)
    }
  }

  const handleMarkerTypeClick = (type) => {
    if (selectedMarkerType?.type === type.type) {
      setSelectedMarkerType(null) // Exit "Add Mode"
      onExitAddMode()
    } else {
      setSelectedMarkerType(type) // Enter "Add Mode"
      onSelectMarkerType(type)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onToggle}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto">
        <SheetHeader className="p-4 border-b">
          <SheetTitle>Building Management</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-6 p-6">
          {/* 1. Address Geocoding */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Find Building</h3>
            <div className="flex gap-2">
              <Input
                type="text"
                value={address}
                onChange={handleAddressChange}
                onKeyDown={handleAddressKeyPress}
                placeholder="Enter building address"
                className="flex-grow"
              />
              <Button onClick={handleGeocodeClick}>Find</Button>
            </div>
          </div>

          <Separator />

          {/* 2. Marker Type Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Add Markers</h3>
            <p className="text-sm text-muted-foreground">Select a marker type and click on the map to add it.</p>
            <div className="flex flex-wrap gap-3">
              {markerTypes.map((mt) => (
                <Button
                  key={mt.type}
                  variant={selectedMarkerType?.type === mt.type ? "default" : "outline"}
                  onClick={() => handleMarkerTypeClick(mt)}
                  className="flex items-center gap-2"
                >
                  <mt.icon size={18} />
                  {mt.type}
                </Button>
              ))}
            </div>
            {selectedMarkerType && (
              <Button variant="secondary" onClick={() => handleMarkerTypeClick(selectedMarkerType)}>
                Done Adding
              </Button>
            )}
          </div>

          <Separator />

          {/* 3. Marker Property Editor (Conditional) */}
          {selectedMarker && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Edit Marker</h3>
              <p className="text-sm text-muted-foreground">
                Editing: <span className="font-semibold">{selectedMarker.id}</span>
              </p>

              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="markerTitle">
                      Title / ID <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="markerTitle"
                      name="title"
                      value={selectedMarker.title || ""}
                      onChange={(e) => onUpdateMarker({ id: selectedMarker.id, title: e.target.value })}
                      required
                      placeholder="e.g., Waste Bin 1"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="markerDescription">Description</Label>
                    <Textarea
                      id="markerDescription"
                      name="description"
                      rows={3}
                      value={selectedMarker.description || ""}
                      onChange={(e) => onUpdateMarker({ id: selectedMarker.id, description: e.target.value })}
                      placeholder="e.g., Main waste collection point"
                    />
                  </div>

                  <Button variant="destructive" onClick={() => onDeleteMarker(selectedMarker.id)} className="w-full">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Marker
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          <Separator />

          {/* 4. Save Action */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Save Changes</h3>
            <Button variant="default" onClick={onSave} className="w-full">
              Save All Markers
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default FacilityDrawer
